/**
 * Offline-first Incident Service
 *
 * Field workers need to start incidents from the scene before connectivity
 * arrives. This service mirrors the damage-assessment pattern: writes go to
 * IndexedDB first, queue with `is_synced=false`, and replay when the sync
 * orchestrator fires.
 *
 * Conflict resolution follows the same shape as damage assessments — if a
 * local incident has pending edits and the server returns a newer version,
 * we store the server payload in `server_snapshot` and let the UI prompt
 * the user to resolve.
 */
import { db, CachedIncident, PendingMutation } from './db';
import { mustDeferSend, sendTenantOf } from './client-id';
import { unwrapEntity, unwrapServerId } from './unwrap';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { useIncidentStore } from '@/store/incident.store';

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

function generateOfflineId(): string {
  return `offline_${crypto.randomUUID()}`;
}

function newPlaceholderNumber(): string {
  // Server overwrites this when it issues a real incident_number; we just
  // need something stable for offline display.
  const ts = Date.now().toString(36).toUpperCase();
  return `OFFLINE-${ts}`;
}

export interface CreateIncidentInput {
  tenant_id: string;
  name: string;
  short_name?: string;
  type: string;
  status?: string;
  classification?: string;
  complexity_level?: string;
  description?: string;
  objectives?: string;
  center_lat?: number;
  center_lng?: number;
  jurisdiction_ids?: string[];
  parent_incident_id?: string;
  start_time?: string;
  end_time?: string;
  operational_period_duration_minutes?: number;
  metadata?: Record<string, unknown>;
}

export interface UpdateIncidentInput extends Partial<Omit<CreateIncidentInput, 'tenant_id'>> {}

class IncidentOfflineService {
  async create(input: CreateIncidentInput): Promise<CachedIncident> {
    const now = new Date().toISOString();
    const offlineId = generateOfflineId();
    const incident: CachedIncident = {
      id: offlineId,
      offline_id: offlineId,
      tenant_id: input.tenant_id,
      incident_number: newPlaceholderNumber(),
      name: input.name,
      short_name: input.short_name,
      type: input.type,
      status: input.status || 'planned',
      classification: input.classification || 'internal',
      complexity_level: input.complexity_level || 'Type 5',
      description: input.description,
      objectives: input.objectives,
      center_lat: input.center_lat,
      center_lng: input.center_lng,
      jurisdiction_ids: input.jurisdiction_ids || [],
      parent_incident_id: input.parent_incident_id,
      start_time: input.start_time || now,
      end_time: input.end_time,
      operational_period_duration_minutes: input.operational_period_duration_minutes,
      metadata: input.metadata || {},
      created_at: now,
      updated_at: now,
      is_synced: false,
      pending_changes: false,
      conflict_state: 'none',
    };

    await db.cachedIncidents.put(incident);

    // Best-effort immediate sync if online; sync orchestrator will retry
    // otherwise.
    if (networkService.isOnline()) {
      void this.syncIncident(incident.id).catch(() => {
        /* will be retried by the orchestrator */
      });
    }

    return incident;
  }

  async update(id: string, input: UpdateIncidentInput): Promise<CachedIncident | null> {
    const existing = await db.cachedIncidents.get(id);
    if (!existing) return null;

    const updated: CachedIncident = {
      ...existing,
      ...input,
      jurisdiction_ids: input.jurisdiction_ids ?? existing.jurisdiction_ids,
      metadata: input.metadata ?? existing.metadata,
      updated_at: new Date().toISOString(),
      is_synced: false,
      pending_changes: true,
    };

    await db.cachedIncidents.put(updated);

    if (networkService.isOnline()) {
      void this.syncIncident(updated.id).catch(() => {
        /* will be retried by the orchestrator */
      });
    }

    return updated;
  }

  async getAll(tenantId: string): Promise<CachedIncident[]> {
    return db.cachedIncidents
      .where('tenant_id')
      .equals(tenantId)
      .reverse()
      .sortBy('updated_at');
  }

  async getById(id: string): Promise<CachedIncident | undefined> {
    const direct = await db.cachedIncidents.get(id);
    if (direct) return direct;
    return db.cachedIncidents.where('offline_id').equals(id).first();
  }

  async getPendingCount(): Promise<number> {
    return db.cachedIncidents.filter(i => !i.is_synced).count();
  }

  async cacheFromServer(tenantId: string): Promise<{ cached: number; conflicts: number }> {
    const result = { cached: 0, conflicts: 0 };

    try {
      const response = await api.get<{ data: any[] }>('/incidents');
      const serverIncidents = response.data || [];

      for (const server of serverIncidents) {
        const existing = await db.cachedIncidents.get(server.id);
        const mapped = mapServerToCache(tenantId, server);

        if (!existing) {
          await db.cachedIncidents.put(mapped);
          result.cached++;
        } else if (existing.pending_changes) {
          await db.cachedIncidents.update(existing.id, {
            conflict_state: 'detected',
            server_updated_at: server.updated_at,
            server_snapshot: server,
          });
          result.conflicts++;
        } else if (new Date(server.updated_at) > new Date(existing.updated_at)) {
          await db.cachedIncidents.put(mapped);
          result.cached++;
        }
      }
    } catch (error) {
      console.warn('[Incident] Failed to cache from server:', error);
    }

    return result;
  }

  /**
   * Single-flight per incident (P2-05). create()/update() start a sync and the
   * orchestrator starts another; two concurrent syncs of one offline incident
   * both saw it unsynced and both POSTed it, creating two server incidents with
   * the children remapped onto only one of them.
   */
  syncIncident(id: string): Promise<boolean> {
    const running = this.inFlight.get(id);
    if (running) return running;
    const run = this.syncIncidentOnce(id).finally(() => {
      this.inFlight.delete(id);
    });
    this.inFlight.set(id, run);
    return run;
  }

  private inFlight = new Map<string, Promise<boolean>>();

  private async syncIncidentOnce(id: string): Promise<boolean> {
    const incident = await this.getById(id);
    if (!incident || incident.is_synced) return true;
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(incident)) return false;

    try {
      let serverId = incident.id;
      const payload = buildServerPayload(incident);

      if (incident.offline_id && incident.id === incident.offline_id) {
        // Create on server
        // POST /incidents returns the BARE incident (incident.controller.ts:25
        // does res.status(201).json(incident)), not { data: ... }. Reading
        // response.data.id threw here after the server had already created the
        // incident, so the record stayed unsynced and every subsequent sync
        // created ANOTHER duplicate incident — and the child FK remap below
        // never ran, orphaning offline tasks and damage assessments.
        const response = await api.post<unknown>('/incidents', payload, sendTenantOf(incident));
        const created = unwrapEntity<{ id?: string; incident_number?: string }>(response);
        const newId = unwrapServerId(response);
        if (!newId) {
          throw new Error('Incident create returned no server id — refusing to mark synced');
        }
        serverId = newId;
        const oldOfflineId = incident.id;

        // Atomic offline→server id swap + cross-entity FK remap. Children created
        // under the offline incident (tasks, damage assessments, etc.) still hold
        // the offline incident_id; remap them here so they don't POST a stale id
        // when they sync (the orchestrator syncs incidents before dependents, so
        // this remap lands first).
        await db.transaction(
          'rw',
          remapTables(),
          async () => {
            await db.cachedIncidents.delete(oldOfflineId);
            await db.cachedIncidents.put({
              ...incident,
              id: serverId,
              incident_number: created?.incident_number || incident.incident_number,
              is_synced: true,
              synced_at: new Date().toISOString(),
              pending_changes: false,
              last_error: undefined,
            });

            if (serverId !== oldOfflineId) {
              await remapIncidentId(oldOfflineId, serverId);
            }
          }
        );
        if (serverId !== oldOfflineId) remapActiveIncident(oldOfflineId, serverId);
      } else {
        // Update on server
        await api.patch(`/incidents/${incident.id}`, payload, sendTenantOf(incident));
        await db.cachedIncidents.update(incident.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      }
      return true;
    } catch (err: any) {
      await db.cachedIncidents.update(incident.id, {
        last_error: err?.message || 'Sync failed',
      });
      return false;
    }
  }

  async syncAll(): Promise<{ synced: number; failed: number; errors: string[] }> {
    const pending = await db.cachedIncidents
      .filter(i => !i.is_synced)
      .toArray();
    const errors: string[] = [];
    let synced = 0;
    let failed = 0;

    // Cross-tenant replay guard: never replay an incident against a different
    // tenant than the one it was created under (the api client injects the
    // ambient X-Tenant-ID). Defer foreign-tenant incidents; they sync when their
    // tenant is active again. Records lacking tenant_id are not skipped.
    const activeTenantId = getActiveTenantId();

    for (const incident of pending) {
      // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
      if (mustDeferSend(incident as never)) continue;
      if (
        incident.tenant_id != null &&
        activeTenantId != null &&
        incident.tenant_id !== activeTenantId
      ) {
        continue;
      }
      const ok = await this.syncIncident(incident.id);
      if (ok) {
        synced++;
      } else {
        failed++;
        if (incident.last_error) errors.push(`${incident.name}: ${incident.last_error}`);
      }
    }

    return { synced, failed, errors };
  }
}

/**
 * Every table whose rows can name an offline-created incident. They are all in
 * the swap transaction, so the incident's id change and the remap of its
 * dependents commit (or roll back) together.
 */
function remapTables() {
  return [
    db.cachedIncidents,
    db.cachedTasks,
    db.cachedDamageAssessments,
    db.cachedSupplyRequests,
    db.offlineSupplyRequests,
    db.cachedCriticalInfrastructure,
    db.cachedRoadClosures,
    db.cachedUtilityOutages,
    db.cachedFemaCosts,
    db.cachedBoardPosts,
    db.cachedUnitLogs,
    db.offlineForms,
    db.formDrafts,
    db.pendingMutations,
  ];
}

/**
 * Remap a (now-synced) incident's offline id to its server id on every row
 * that names it. Must be called inside a transaction over remapTables().
 *
 * P2-05 fixes (each was a dependent that kept POSTing the dead offline id,
 * which the server rejects as a non-UUID, so the record never synced):
 *  - supply requests (legacy cache) and infrastructure rows are POSTed from
 *    their nested `data` object, so `data.incident_id` is remapped too, not
 *    only the indexed top-level copy;
 *  - offline-created supply requests (offlineSupplyRequests), offline ICS forms
 *    (offlineForms) and form drafts (formDrafts) were not remapped at all;
 *  - a child incident created offline under an offline parent kept the dead
 *    `parent_incident_id`;
 *  - queued mutations whose body names the incident.
 */
async function remapIncidentId(oldOfflineId: string, newServerId: string): Promise<void> {
  // Rows that carry incident_id only at the top level (indexed).
  const topLevel = [
    db.cachedTasks,
    db.cachedDamageAssessments,
    db.offlineSupplyRequests,
    db.cachedFemaCosts,
    db.cachedBoardPosts,
    db.cachedUnitLogs,
    db.offlineForms,
    db.formDrafts,
  ];
  for (const table of topLevel) {
    await (table as typeof db.cachedTasks)
      .where('incident_id')
      .equals(oldOfflineId)
      .modify({ incident_id: newServerId });
  }

  // Rows POSTed from a nested `data` object: remap both copies.
  const nested = [
    db.cachedSupplyRequests,
    db.cachedCriticalInfrastructure,
    db.cachedRoadClosures,
    db.cachedUtilityOutages,
  ];
  for (const table of nested) {
    await (table as typeof db.cachedRoadClosures)
      .where('incident_id')
      .equals(oldOfflineId)
      .modify((row: { incident_id?: string | null; data?: Record<string, unknown> | null }) => {
        row.incident_id = newServerId;
        if (row.data && typeof row.data === 'object' && row.data.incident_id === oldOfflineId) {
          row.data = { ...row.data, incident_id: newServerId };
        }
      });
  }

  // A child incident created offline under this (offline) parent.
  await db.cachedIncidents
    .filter((i) => i.parent_incident_id === oldOfflineId)
    .modify({ parent_incident_id: newServerId });

  // Queued mutations naming the incident in their body or endpoint.
  await db.pendingMutations
    .filter((m) => m.body?.incident_id === oldOfflineId || m.endpoint.includes(oldOfflineId))
    .modify((m: PendingMutation) => {
      if (m.body?.incident_id === oldOfflineId) m.body = { ...m.body, incident_id: newServerId };
      m.endpoint = m.endpoint.split(oldOfflineId).join(newServerId);
    });
}

/** Point the engaged incident at its server id once the offline one synced. */
function remapActiveIncident(oldOfflineId: string, newServerId: string): void {
  try {
    const state = useIncidentStore.getState();
    if (state.activeIncidentId !== oldOfflineId) return;
    if (state.activeIncident) {
      state.setActiveIncident({ ...state.activeIncident, id: newServerId });
    } else {
      state.adoptServerContext(newServerId);
    }
  } catch {
    // The store is UI state; resolveSyncableIncidentId still maps stale ids.
  }
}

export type IncidentIdResolution =
  | { defer: false; incidentId: string | null }
  | { defer: true; incidentId: null };

/**
 * The incident id a dependent record may be sent with.
 *
 *  - a server id (or none) is sent as is;
 *  - an offline id whose incident has synced maps to the server id. This
 *    catches a child that picked up the offline id AFTER the remap ran (for
 *    example from the engaged incident in a store that still held it);
 *  - an offline id whose incident has NOT synced yet means: defer. Sending it
 *    would be rejected as a non-UUID, and some queues treat a 4xx as permanent.
 */
export async function resolveSyncableIncidentId(
  incidentId: string | null | undefined,
): Promise<IncidentIdResolution> {
  if (!incidentId) return { defer: false, incidentId: null };
  if (!incidentId.startsWith('offline_')) return { defer: false, incidentId };
  const swapped = await db.cachedIncidents.where('offline_id').equals(incidentId).first();
  if (swapped && swapped.is_synced && swapped.id !== incidentId) {
    return { defer: false, incidentId: swapped.id };
  }
  return { defer: true, incidentId: null };
}

function mapServerToCache(tenantId: string, server: any): CachedIncident {
  return {
    id: server.id,
    tenant_id: tenantId,
    incident_number: server.incident_number || '',
    name: server.name,
    short_name: server.short_name,
    type: server.type,
    status: server.status,
    classification: server.classification,
    complexity_level: server.complexity_level,
    description: server.description,
    objectives: server.objectives,
    center_lat: server.center_lat,
    center_lng: server.center_lng,
    jurisdiction_ids: server.jurisdiction_ids || [],
    parent_incident_id: server.parent_incident_id,
    start_time: server.start_time,
    end_time: server.end_time,
    operational_period_duration_minutes: server.operational_period_duration_minutes,
    metadata: server.metadata || {},
    created_at: server.created_at,
    updated_at: server.updated_at,
    is_synced: true,
    synced_at: new Date().toISOString(),
    pending_changes: false,
    conflict_state: 'none',
    server_updated_at: server.updated_at,
  };
}

function buildServerPayload(incident: CachedIncident): Record<string, unknown> {
  return {
    name: incident.name,
    short_name: incident.short_name,
    type: incident.type,
    status: incident.status,
    classification: incident.classification,
    complexity_level: incident.complexity_level,
    description: incident.description,
    objectives: incident.objectives,
    center_lat: incident.center_lat,
    center_lng: incident.center_lng,
    jurisdiction_ids: incident.jurisdiction_ids,
    parent_incident_id: incident.parent_incident_id,
    start_time: incident.start_time,
    end_time: incident.end_time,
    operational_period_duration_minutes: incident.operational_period_duration_minutes,
    metadata: incident.metadata,
  };
}

export const incidentOfflineService = new IncidentOfflineService();
